import Accordion from '@service-victoria/component-library/components/Accordion';
import Checkbox from '@service-victoria/component-library/components/Checkbox';
import List from '@service-victoria/component-library/components/List';
import Notification from '@service-victoria/component-library/components/Notification';
import Text from '@service-victoria/component-library/components/Text';
import { StepComponent } from '@service-victoria/component-library/components/Wizard/props';
import SummaryItem from 'Components/SummaryItem';
import * as Interfaces from 'interfaces';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { constants, validatePreviousStep } from 'utils';
import * as Styled from '../styled';
import { getStreetAddress } from './helpers';
import { TextBodyWithInlineLink } from 'steps/styled';

const cardLabel = (type?: string) => {
  switch (type) {
    case 'driver_license':
      return 'Driver licence number';
    case 'seniorsCard':
      return 'Seniors card number';
    case 'proofOfAgeCard':
      return 'Proof of age card number';
    default:
      return '';
  }
};

export type ComponentProps = StepComponent<Pick<Interfaces.Common.FormValues, 'review'>>;

const Review: React.FC<ComponentProps> = ({ context }) => {
  const { values: valuesRaw } = context.form;
  const vRaw = valuesRaw as Interfaces.Common.FormValues;

  React.useEffect(() => {
    validatePreviousStep('review', context);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const history = useHistory();
  const { currentState } = context.appStates[
    constants.StorageKeys.State
  ] as Interfaces.Store.AppContext;
  const [errorCount, setErrorCount] = React.useState(0);
  const homeStreetAddress = getStreetAddress(vRaw.abn.address?.fullAddress);

  React.useEffect(() => {
    if (currentState.error) {
      setErrorCount(errorCount => errorCount + 1);
    }
  }, [currentState.error]);

  React.useEffect(() => {
    if (errorCount >= 3) {
      history.push(constants.Routes.TechnicalError);
    }
  }, [errorCount, history]);

  // expanded this ternary out into a more readable conditional logic
  let businessName: string;
  if (!!vRaw.abn.optionalBusinessName) {
    businessName = vRaw.abn.optionalBusinessName;
  } else if (vRaw.abn.name === '~ Service Error ~' || vRaw.abn.name === '') {
    businessName = vRaw.abn.optionalBusinessName || '';
  } else {
    businessName = vRaw.abn.name as string;
  }

  return (
    <Styled.Wrapper>
      <Text.Heading.H1 fontWeight={600}>Review your details</Text.Heading.H1>
      <Styled.Spacer24 />
      <Text.Body>
        Check your details and edit any incorrect info before submitting your application.
      </Text.Body>
      {!!currentState.error && (
        <Styled.Error>
          <Notification.Error.Text
            title={currentState.error?.title ?? ''}
            value={currentState.error?.value ?? ''}
          />
          <Styled.Spacer48 />
        </Styled.Error>
      )}
      <Accordion
        items={[
          {
            id: 'business-details',
            title: 'Business details',
            component: (
              <>
                <SummaryItem
                  label='This voucher wil be used in'
                  value={vRaw.abn.voucherType === 'metro' ? 'Melbourne Metro' : 'Regional Victoria'}
                />
                <SummaryItem label='ABN' value={vRaw.abn.number} />
                {businessName && <SummaryItem label='Business name' value={businessName} />}
                <SummaryItem label='Street address' value={homeStreetAddress} />
                <SummaryItem label='Suburb/Town' value={vRaw.abn.address.suburb} />
                <SummaryItem label='State' value={vRaw.abn.address.state} />
                <SummaryItem label='Postcode' value={vRaw.abn.address.postCode} />
              </>
            )
          },
          {
            id: 'personal-details',
            title: 'Your personal details',
            component: (
              <>
                <SummaryItem label='Given name' value={vRaw.identity.givenName} />
                <SummaryItem label='Family name' value={vRaw.identity.familyName} />
                <SummaryItem label='Email address' value={vRaw.identity.email} />
                <SummaryItem label='Mobile number' value={vRaw.identity.mobileNumber} />
                {vRaw.identity.proofType === 'driver_license' && (
                  <SummaryItem
                    label='Name on driver licence'
                    value={[
                      vRaw.identity.givenName,
                      vRaw.identity.middleName,
                      vRaw.identity.familyName
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  />
                )}
                <SummaryItem
                  label={cardLabel(vRaw.identity.proofType)}
                  value={vRaw.identity.cardNo}
                />
                <SummaryItem label='Date of birth' value={vRaw.identity.dob} />
              </>
            )
          }
        ]}
      />
      <Styled.Spacer48 />
      <Text.Heading.H3 fontWeight={600}>Declaration</Text.Heading.H3>
      <Styled.Spacer24 />
      <Text.Body>
        I,{' '}
        <strong>
          {vRaw.identity?.givenName} {vRaw.identity?.familyName}
        </strong>{' '}
        declare:
      </Text.Body>
      <List
        type='disc'
        items={[
          "I'm authorised to apply on behalf of this business",
          'all information given is true and correct. I understand that if the information is wrong my application won’t be successful',
          <Text.Link
            key='link'
            href='https://www.vic.gov.au/covidsafe-outdoor-activation-voucher-program'
            value="I've read the program guidelines and understand that if my business doesn’t meet the criteria, you can’t give my business a voucher"
          />,
          "I understand there's a limited number of vouchers. My business needs to be eligible and vouchers need to be available for my business to get a voucher",
          'my business has a workable plan to move indoor activities outdoors safely. This includes getting permits and meeting regulation requirements',
          'my plan includes spending $2,000 or more on eligible items',
          'my business will spend the $2,000 voucher payment within 3 months of getting it.'
        ]}
      />
      <Text.Body>
        <TextBodyWithInlineLink>
          Successful applicants must provide proof that they have undertaken the work (as per their
          workplan) at three months following receipt of the voucher. Failure to do so would
          constitute a failure to comply with the terms of funding as set out in{' '}
          <Text.Link
            href='https://www.vic.gov.au/covidsafe-outdoor-activation-voucher-program'
            value='the program guidelines'
          />{' '}
          and result in the grant being repayable on demand.{' '}
        </TextBodyWithInlineLink>
      </Text.Body>
      <Checkbox name='review.consentDeclared' label='I agree' />
    </Styled.Wrapper>
  );
};

export default Review;
